// IndexedDB-backed offline queue for inventory entries.
// Uses raw IDB (no library) to keep deps light.
const DB_NAME = "inventory_poh";
const DB_VERSION = 1;
const STORE_QUEUE = "queue";
const STORE_CACHE = "cache";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: "client_uuid" });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(storeName, mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    const result = fn(store);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
  });
}

// -------- Queue (offline entries) --------
export async function queueEntry(sessionId, entry) {
  const item = { ...entry, session_id: sessionId, _queued_at: new Date().toISOString() };
  await tx(STORE_QUEUE, "readwrite", (s) => s.put(item));
  return item;
}

export async function getQueue() {
  return new Promise(async (resolve) => {
    const db = await openDB();
    const t = db.transaction(STORE_QUEUE, "readonly");
    const req = t.objectStore(STORE_QUEUE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

export async function removeFromQueue(clientUuid) {
  await tx(STORE_QUEUE, "readwrite", (s) => s.delete(clientUuid));
}

export async function clearQueue() {
  await tx(STORE_QUEUE, "readwrite", (s) => s.clear());
}

// -------- Cache (last-known items / dashboard) --------
export async function setCache(key, value) {
  await tx(STORE_CACHE, "readwrite", (s) => s.put({ key, value, updated_at: new Date().toISOString() }));
}

export async function getCache(key) {
  return new Promise(async (resolve) => {
    const db = await openDB();
    const t = db.transaction(STORE_CACHE, "readonly");
    const req = t.objectStore(STORE_CACHE).get(key);
    req.onsuccess = () => resolve(req.result?.value ?? null);
    req.onerror = () => resolve(null);
  });
}

export function uuid() {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}
